import type { ConversationDeckCacheState } from "./conversation-deck-cache-core";

const DATABASE_NAME = "opncue-conversation-deck";
const STORE_NAME = "cache";
const CACHE_KEY = "active";

let databasePromise: Promise<IDBDatabase> | null = null;

function database(): Promise<IDBDatabase> {
  if (!databasePromise) {
    databasePromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DATABASE_NAME, 1);
      request.onupgradeneeded = () => {
        if (!request.result.objectStoreNames.contains(STORE_NAME)) {
          request.result.createObjectStore(STORE_NAME);
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }
  return databasePromise;
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onabort = () =>
      reject(transaction.error ?? new Error("Conversation Deck cache aborted."));
    transaction.onerror = () =>
      reject(transaction.error ?? new Error("Conversation Deck cache failed."));
  });
}

export const conversationDeckPersistence = {
  async read(): Promise<ConversationDeckCacheState | null> {
    const db = await database();
    const transaction = db.transaction(STORE_NAME, "readonly");
    const request = transaction.objectStore(STORE_NAME).get(CACHE_KEY);
    const result = await new Promise<ConversationDeckCacheState | null>(
      (resolve, reject) => {
        request.onsuccess = () =>
          resolve((request.result as ConversationDeckCacheState) ?? null);
        request.onerror = () => reject(request.error);
      },
    );
    await transactionDone(transaction);
    return result;
  },

  async mutate(
    transform: (
      state: ConversationDeckCacheState | null,
    ) => ConversationDeckCacheState | null,
  ): Promise<ConversationDeckCacheState | null> {
    const db = await database();
    const transaction = db.transaction(STORE_NAME, "readwrite");
    const store = transaction.objectStore(STORE_NAME);
    const getRequest = store.get(CACHE_KEY);
    const result = await new Promise<ConversationDeckCacheState | null>(
      (resolve, reject) => {
        getRequest.onsuccess = () => {
          try {
            const next = transform(
              (getRequest.result as ConversationDeckCacheState) ?? null,
            );
            if (next) store.put(next, CACHE_KEY);
            else store.delete(CACHE_KEY);
            resolve(next);
          } catch (error) {
            transaction.abort();
            reject(error);
          }
        };
        getRequest.onerror = () => reject(getRequest.error);
      },
    );
    await transactionDone(transaction);
    return result;
  },

  async clear(): Promise<void> {
    await this.mutate(() => null);
  },
};
